import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useClientAuth, clientApiRequest } from '../../contexts/ClientAuthContext';

// ─── Design Tokens ────────────────────────────────────────────────────────────
const NAVY        = '#0D2C54';
const GOLD        = '#D4A853';
const GREEN       = '#2E7D32';
const RED         = '#C62828';
const LIGHT_BG    = '#F8F9FA';
const CARD_BG     = '#FFFFFF';
const TEXT_PRIMARY   = '#0D2C54';
const TEXT_SECONDARY = '#6B7A8D';
const TEXT_MUTED     = '#9aa5b4';
const BORDER         = '#E4E8EF';
const CHARGE_COLOR   = '#C62828';
const PAYMENT_COLOR  = '#2E7D32';

// ─── Formatters ───────────────────────────────────────────────────────────────
const fmtCurrency = (n) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(n || 0);

const fmtCurrencyCompact = (n) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n || 0);

const fmtDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const fmtMsgTime = (d) => {
  if (!d) return '';
  const date = new Date(d);
  const now   = new Date();
  const diffDays = Math.floor((now - date) / 86400000);
  if (diffDays === 0) return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7)   return date.toLocaleDateString('en-US', { weekday: 'short' });
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

// ─── Global Styles ────────────────────────────────────────────────────────────
const GLOBAL_CSS = `
  @keyframes fadeIn  { from { opacity:0; transform:translateY(10px); } to { opacity:1; transform:translateY(0); } }
  @keyframes spin    { to   { transform:rotate(360deg); } }
  @keyframes pulse   { 0%,100% { opacity:1; } 50% { opacity:0.5; } }

  * { box-sizing: border-box; }

  /* ── Ledger table ── */
  .ledger-table { width:100%; border-collapse:collapse; }
  .ledger-table thead th {
    padding: 10px 14px;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 1.4px;
    text-transform: uppercase;
    color: ${TEXT_MUTED};
    border-bottom: 1px solid ${BORDER};
    background: ${LIGHT_BG};
    white-space: nowrap;
  }
  .ledger-table thead th.right { text-align:right; }
  .ledger-table tbody tr { transition: background 0.1s; cursor:default; }
  .ledger-table tbody tr:hover { background: #F0F4FF !important; }
  .ledger-table tbody td {
    padding: 13px 14px;
    font-size: 14px;
    color: ${TEXT_PRIMARY};
    border-bottom: 1px solid ${BORDER};
    vertical-align: middle;
  }
  .ledger-table tbody td.right { text-align:right; font-variant-numeric:tabular-nums; }

  /* ── Scope table ── */
  .scope-table { width:100%; border-collapse:collapse; }
  .scope-table thead th {
    padding: 10px 14px;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 1.4px;
    text-transform: uppercase;
    color: ${TEXT_MUTED};
    border-bottom: 1px solid ${BORDER};
    background: ${LIGHT_BG};
  }
  .scope-table thead th.right { text-align:right; }
  .scope-table tbody tr:nth-child(even) { background: ${LIGHT_BG}; }
  .scope-table tbody tr:hover { background: #F0F4FF !important; }
  .scope-table tbody td {
    padding: 12px 14px;
    font-size: 14px;
    color: ${TEXT_PRIMARY};
    border-bottom: 1px solid ${BORDER};
  }
  .scope-table tbody td.right { text-align:right; font-variant-numeric:tabular-nums; }

  /* ── Mobile ── */
  @media (max-width: 640px) {
    .ledger-col-date  { display: none; }
    .ledger-mobile-date { display: block !important; }
    .ledger-table thead th.hide-mobile { display:none; }
    .ledger-table tbody td.hide-mobile { display:none; }
    .hero-amount { font-size: 44px !important; }
    .summary-line { flex-direction:column !important; gap:4px !important; }
  }
`;

// ─── MessageThread ────────────────────────────────────────────────────────────
function MessageThread({ employeeArCode }) {
  const [messages, setMessages]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [msgText, setMsgText]     = useState('');
  const [sending, setSending]     = useState(false);
  const bottomRef   = useRef(null);
  const textareaRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const qs = employeeArCode ? `?ar_code=${encodeURIComponent(employeeArCode)}` : '';

  const loadMessages = useCallback(async () => {
    try {
      const res = await clientApiRequest.get(`/api/client/messages${qs}`);
      setMessages(res.data.messages || []);
    } catch (err) {
      console.error('Failed to load messages:', err);
    } finally {
      setLoading(false);
    }
  }, [qs]);

  useEffect(() => { loadMessages(); }, [loadMessages]);
  // Poll for new messages every 15 seconds
  useEffect(() => {
    const interval = setInterval(() => { loadMessages(); }, 15000);
    return () => clearInterval(interval);
  }, [loadMessages]);
  useEffect(() => { if (!loading) scrollToBottom(); }, [messages, loading, scrollToBottom]);

  const handleSend = async () => {
    const trimmed = msgText.trim();
    if (!trimmed || sending) return;
    setSending(true);
    try {
      const body = { message: trimmed };
      if (employeeArCode) body.ar_code = employeeArCode;
      const res = await clientApiRequest.post('/api/client/messages', body);
      setMessages(prev => [...prev, res.data.message]);
      setMsgText('');
      if (textareaRef.current) textareaRef.current.style.height = 'auto';
    } catch (err) {
      console.error('Failed to send:', err);
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const handleTextareaChange = (e) => {
    setMsgText(e.target.value);
    e.target.style.height = 'auto';
    e.target.style.height = Math.min(e.target.scrollHeight, 100) + 'px';
  };

  return (
    <div style={{
      background: CARD_BG, borderRadius: '12px',
      border: `1px solid ${BORDER}`, boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
      marginBottom: '24px', overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{
        padding: '18px 24px', borderBottom: `1px solid ${BORDER}`,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <div style={{ fontSize: '11px', fontWeight: 700, color: TEXT_SECONDARY, letterSpacing: '1.5px', textTransform: 'uppercase' }}>
          Messages
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: GREEN, fontWeight: 500 }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: GREEN }} />
          ACS AfterCare
        </div>
      </div>

      {/* Message area */}
      <div style={{
        height: '340px', overflowY: 'auto', padding: '16px 20px',
        display: 'flex', flexDirection: 'column', gap: '12px',
      }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: TEXT_MUTED, fontSize: 13 }}>
            Loading messages…
          </div>
        ) : messages.length === 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', textAlign: 'center', color: TEXT_MUTED, gap: 8 }}>
            <div style={{ fontSize: 36 }}>💬</div>
            <div style={{ fontSize: 14, fontWeight: 500, color: TEXT_SECONDARY }}>Start a conversation with our team.</div>
            <div style={{ fontSize: 13, color: TEXT_MUTED, maxWidth: 260, lineHeight: 1.5 }}>We typically respond within 1 business day.</div>
          </div>
        ) : (
          messages.map((msg) => {
            const isClient = msg.direction === 'inbound';
            return (
              <div key={msg.id} style={{
                display: 'flex', flexDirection: 'column',
                alignItems: isClient ? 'flex-end' : 'flex-start',
                maxWidth: '78%',
                marginLeft: isClient ? 'auto' : undefined,
              }}>
                <div style={{
                  background: isClient ? NAVY : '#F0F2F5',
                  color: isClient ? '#fff' : TEXT_PRIMARY,
                  borderRadius: isClient ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                  padding: '10px 14px', fontSize: 14, lineHeight: 1.5, wordBreak: 'break-word',
                }}>
                  {msg.message}
                </div>
                <div style={{
                  fontSize: 11, color: TEXT_MUTED, marginTop: 4,
                  display: 'flex', alignItems: 'center', gap: 6,
                  justifyContent: isClient ? 'flex-end' : 'flex-start',
                }}>
                  <span>{isClient ? 'You' : (msg.author_name || 'ACS Team')}</span>
                  <span>·</span>
                  <span>{fmtMsgTime(msg.created_at)}</span>
                  {isClient && (
                    <>
                      <span>·</span>
                      <span style={{ fontStyle: 'italic' }}>{msg.read_at ? '✓✓ Read' : '✓ Delivered'}</span>
                    </>
                  )}
                </div>
              </div>
            );
          })
        )}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div style={{
        padding: '12px 16px', borderTop: `1px solid ${BORDER}`,
        display: 'flex', gap: 10, alignItems: 'flex-end', background: LIGHT_BG,
      }}>
        <textarea
          ref={textareaRef}
          style={{
            flex: 1, padding: '10px 14px',
            border: `1px solid ${msgText ? NAVY : BORDER}`,
            borderRadius: '20px', fontSize: 14, fontFamily: 'inherit',
            outline: 'none', resize: 'none', lineHeight: 1.4,
            maxHeight: 100, minHeight: 40, color: TEXT_PRIMARY,
            background: '#fff', transition: 'border-color 0.2s',
          }}
          placeholder="Type a message… (Enter to send, Shift+Enter for new line)"
          value={msgText}
          onChange={handleTextareaChange}
          onKeyDown={handleKeyDown}
          rows={1}
        />
        <button
          style={{
            width: 40, height: 40, borderRadius: '50%',
            background: (!msgText.trim() || sending) ? '#c8d0da' : NAVY,
            color: '#fff', border: 'none',
            cursor: (!msgText.trim() || sending) ? 'not-allowed' : 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 16, flexShrink: 0, transition: 'background 0.2s',
          }}
          onClick={handleSend}
          disabled={!msgText.trim() || sending}
        >
          {sending ? '…' : '↑'}
        </button>
      </div>
    </div>
  );
}

// ─── Job Grouped Statement ────────────────────────────────────────────────────
function JobGroupedStatement({ scopeJobs }) {
  const [expandedJobs, setExpandedJobs] = useState({});

  const toggleJob = (jobNum) => {
    setExpandedJobs(prev => ({ ...prev, [jobNum]: !prev[jobNum] }));
  };

  // Sort jobs: ones with balance first
  const sortedJobs = (scopeJobs || []).sort((a, b) => {
    if ((a.balance_due || 0) > 0 && (b.balance_due || 0) <= 0) return -1;
    if ((a.balance_due || 0) <= 0 && (b.balance_due || 0) > 0) return 1;
    return (a.description || '').localeCompare(b.description || '');
  });

  if (sortedJobs.length === 0) {
    return (
      <div style={{ padding: 32, textAlign: 'center', color: TEXT_MUTED, fontStyle: 'italic' }}>
        No activity found.
      </div>
    );
  }

  return (
    <div style={{ padding: '0 0 8px' }}>
      {sortedJobs.map((job) => {
        const jn = job.job_number;
        const isOpen = expandedJobs[jn];
        const hasBalance = (job.balance_due || 0) > 0.01;
        const invoices = job.open_invoices || [];

        return (
          <div key={jn} style={{ borderBottom: `1px solid ${BORDER}` }}>
            <button
              onClick={() => toggleJob(jn)}
              style={{
                width: '100%', background: 'none', border: 'none',
                padding: '16px 24px', cursor: 'pointer', fontFamily: 'inherit',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                textAlign: 'left', gap: 16, transition: 'background 0.15s',
              }}
              onMouseEnter={e => e.currentTarget.style.background = LIGHT_BG}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, color: TEXT_PRIMARY, fontSize: 15 }}>
                  {job.description || jn}
                </div>
                <div style={{ fontSize: 12, color: TEXT_MUTED, marginTop: 2 }}>
                  {invoices.length > 0 ? `${invoices.length} open invoice${invoices.length > 1 ? 's' : ''}` : 'Paid in full'}
                </div>
              </div>
              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <div style={{ fontSize: 16, fontWeight: 700, color: hasBalance ? RED : GREEN }}>
                  {hasBalance ? fmtCurrency(job.balance_due) : '\u2713 Paid'}
                </div>
              </div>
              <div style={{ color: TEXT_MUTED, fontSize: 14, flexShrink: 0 }}>
                {isOpen ? '\u25b2' : '\u25bc'}
              </div>
            </button>

            {isOpen && (
              <div style={{ padding: '0 24px 16px 40px' }}>
                {invoices.length > 0 ? (
                  <div style={{ borderTop: `1px solid ${BORDER}`, paddingTop: 12 }}>
                    {invoices.map((inv, i) => (
                      <div key={i} style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        padding: '8px 0', borderBottom: i < invoices.length - 1 ? `1px solid ${BORDER}20` : 'none',
                      }}>
                        <div>
                          <div style={{ fontSize: 14, fontWeight: 500, color: TEXT_PRIMARY }}>
                            Invoice #{inv.invoice_number}
                            {inv.description && <span style={{ color: TEXT_SECONDARY, fontWeight: 400 }}> \u2014 {inv.description}</span>}
                          </div>
                          <div style={{ fontSize: 12, color: TEXT_MUTED, marginTop: 2 }}>
                            {inv.due_date ? `Due ${fmtDate(inv.due_date)}` : ''}
                          </div>
                        </div>
                        <div style={{ fontSize: 14, fontWeight: 600, color: inv.balance > 0 ? RED : GREEN, whiteSpace: 'nowrap' }}>
                          {fmtCurrency(inv.balance)}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ fontSize: 13, color: TEXT_MUTED, fontStyle: 'italic', paddingTop: 8 }}>
                    All invoices for this project have been paid. Thank you.
                  </div>
                )}
                <div style={{
                  display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginTop: 12, paddingTop: 12,
                  borderTop: `1px solid ${BORDER}`,
                }}>
                  {[
                    { label: 'Contract Value', value: job.contract_value, color: TEXT_PRIMARY },
                    { label: 'Amount Paid', value: job.amount_paid, color: GREEN },
                    { label: 'Balance Due', value: job.balance_due, color: job.balance_due > 0 ? RED : GREEN },
                    { label: 'Future Billing', value: job.future_billing, color: TEXT_SECONDARY },
                  ].map(({ label, value, color }) => (
                    <div key={label} style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: 15, fontWeight: 700, color }}>{fmtCurrencyCompact(value)}</div>
                      <div style={{ fontSize: 10, color: TEXT_MUTED, letterSpacing: '0.8px', textTransform: 'uppercase', marginTop: 2 }}>{label}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Main Dashboard ───────────────────────────────────────────────────────────
export default function ClientDashboard() {
  const { clientUser } = useClientAuth();
  const isEmployee = clientUser?.ar_code === '*' || clientUser?.employee === true;

  // ✅ selectedArCode MUST be declared before we read it for effectiveArCode.
  // The previous order tripped a Temporal Dead Zone ReferenceError in the
  // minified bundle ("Cannot access 'y' before initialization") that killed
  // the entire portal tree before any error boundary could catch it. This
  // was the root cause of the white screen on /portal that employees were
  // hitting when they tried to log in. (Tom Clancy 2026-04-30.)
  const [accountsList, setAccountsList]       = useState([]);
  const [selectedArCode, setSelectedArCode]   = useState(() => {
    return isEmployee ? (localStorage.getItem('employee_selected_ar') || '') : '';
  });

  const effectiveArCode = isEmployee ? selectedArCode : clientUser?.ar_code;

  const [ledger, setLedger]           = useState(null);
  const [arDetail, setArDetail]       = useState(null);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState('');
  const [showAll, setShowAll]         = useState(false);
  const [scopeOpen, setScopeOpen]     = useState(false);
  const [arSearch, setArSearch]               = useState('');
  const [showArDropdown, setShowArDropdown]   = useState(false);

  // Payment submission state
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentFile, setPaymentFile]           = useState(null);
  const [paymentNotes, setPaymentNotes]         = useState('');
  const [paymentSubmitting, setPaymentSubmitting] = useState(false);
  const [paymentResult, setPaymentResult]       = useState(null);

  useEffect(() => { document.title = 'ACS Client Portal'; }, []);

  // Load accounts list for employee selector
  useEffect(() => {
    if (!isEmployee) return;
    clientApiRequest.get('/api/client/accounts-list')
      .then(res => setAccountsList(res.data.accounts || []))
      .catch(err => console.error('Failed to load accounts list:', err));
  }, [isEmployee]);

  // Load data — re-runs when selectedArCode changes (for employees)
  useEffect(() => {
    if (isEmployee && !selectedArCode) {
      // Employee but no client selected yet — don't load
      setLoading(false);
      setLedger(null);
      setArDetail(null);
      return;
    }

    setLoading(true);
    setError('');
    const qs = isEmployee ? `?ar_code=${encodeURIComponent(selectedArCode)}` : '';

    const load = async () => {
      try {
        const [ledgerRes, arRes] = await Promise.all([
          clientApiRequest.get(`/api/client/ledger${qs}`),
          clientApiRequest.get(`/api/client/jobs${qs}`).catch(() => null),
        ]);
        setLedger(ledgerRes.data);
        if (arRes?.data) setArDetail(arRes.data);
      } catch (err) {
        setError(err?.response?.data?.error || 'Failed to load account data.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [isEmployee, selectedArCode]);

  const handleSelectAccount = (ac) => {
    setSelectedArCode(ac.ar_code);
    localStorage.setItem('employee_selected_ar', ac.ar_code);
    setArSearch('');
    setShowArDropdown(false);
    setLedger(null);
    setArDetail(null);
  };

  // Filtered accounts list for employee search
  const filteredAccounts = arSearch
    ? accountsList.filter(ac =>
        ac.ar_code.toLowerCase().includes(arSearch.toLowerCase()) ||
        ac.client_name.toLowerCase().includes(arSearch.toLowerCase())
      ).slice(0, 12)
    : accountsList.slice(0, 12);

  const selectedAccount = accountsList.find(ac => ac.ar_code === selectedArCode);

  // ── Employee selector UI helper ──
  const EmployeeAccountSelector = () => (
    <div style={{
      background: '#EEF2F8', border: `1px solid ${BORDER}`,
      borderLeft: `4px solid ${NAVY}`, borderRadius: 10,
      padding: '16px 20px', marginBottom: 24,
    }}>
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '1.5px', textTransform: 'uppercase', color: TEXT_SECONDARY, marginBottom: 10 }}>
        Staff View — Select Client Account
      </div>
      <div style={{ position: 'relative' }}>
        <input
          type="text"
          placeholder="Search by name or AR code…"
          value={arSearch || (selectedAccount ? `${selectedAccount.client_name} (${selectedAccount.ar_code})` : '')}
          onChange={(e) => { setArSearch(e.target.value); setShowArDropdown(true); }}
          onFocus={() => { setArSearch(''); setShowArDropdown(true); }}
          onBlur={() => setTimeout(() => setShowArDropdown(false), 180)}
          style={{
            width: '100%', padding: '10px 14px', fontSize: 14,
            border: `1.5px solid ${showArDropdown ? NAVY : BORDER}`,
            borderRadius: 8, outline: 'none', fontFamily: 'inherit',
            boxSizing: 'border-box', color: TEXT_PRIMARY, background: '#fff',
            boxShadow: showArDropdown ? '0 0 0 3px rgba(29,51,84,0.1)' : 'none',
          }}
        />
        {showArDropdown && filteredAccounts.length > 0 && (
          <div style={{
            position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 200,
            background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 8,
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)', marginTop: 4,
            maxHeight: 260, overflowY: 'auto',
          }}>
            {filteredAccounts.map(ac => (
              <div
                key={ac.ar_code}
                onMouseDown={() => handleSelectAccount(ac)}
                style={{
                  padding: '10px 14px', cursor: 'pointer', fontSize: 14,
                  borderBottom: `1px solid ${BORDER}20`,
                  display: 'flex', gap: 12, alignItems: 'center',
                }}
                onMouseEnter={e => e.currentTarget.style.background = '#F0F4FF'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <span style={{ fontWeight: 600, color: TEXT_PRIMARY, minWidth: 70, fontFamily: '"Courier New", monospace', fontSize: 13 }}>{ac.ar_code}</span>
                <span style={{ color: TEXT_SECONDARY }}>{ac.client_name}</span>
              </div>
            ))}
          </div>
        )}
      </div>
      {selectedAccount && (
        <div style={{ marginTop: 10, fontSize: 13, color: NAVY, fontWeight: 500 }}>
          Viewing as: <strong>{selectedAccount.client_name}</strong> &nbsp;
          <span style={{
            display: 'inline-block', padding: '2px 8px',
            background: NAVY, color: '#fff', borderRadius: 4,
            fontSize: 11, fontWeight: 700, fontFamily: '"Courier New", monospace', letterSpacing: '0.5px',
          }}>{selectedAccount.ar_code}</span>
        </div>
      )}
    </div>
  );

  // ── Loading ──
  if (loading) {
    return (
      <div style={{ animation: 'fadeIn 0.35s ease-out' }}>
        <style>{GLOBAL_CSS}</style>
        {isEmployee && <EmployeeAccountSelector />}
        <div style={{ textAlign: 'center', padding: '80px 24px', color: TEXT_MUTED }}>
          <div style={{
            width: 36, height: 36, border: `3px solid ${BORDER}`,
            borderTop: `3px solid ${NAVY}`, borderRadius: '50%',
            animation: 'spin 0.8s linear infinite', margin: '0 auto 16px',
          }} />
          <div>Loading account…</div>
        </div>
      </div>
    );
  }

  // ── Employee: no client selected yet ──
  if (isEmployee && !selectedArCode) {
    return (
      <div style={{ animation: 'fadeIn 0.35s ease-out' }}>
        <style>{GLOBAL_CSS}</style>
        <EmployeeAccountSelector />
        <div style={{ textAlign: 'center', padding: '64px 24px', color: TEXT_MUTED }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>🔍</div>
          <div style={{ fontSize: 16, fontWeight: 500, color: TEXT_SECONDARY }}>Select a client account above to view their portal.</div>
        </div>
      </div>
    );
  }

  // ── Error ──
  if (error) {
    return (
      <div style={{ animation: 'fadeIn 0.35s ease-out' }}>
        <style>{GLOBAL_CSS}</style>
        {isEmployee && <EmployeeAccountSelector />}
        <div style={{ textAlign: 'center', padding: '64px 24px', color: RED }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>⚠</div>
          <div>{error}</div>
        </div>
      </div>
    );
  }

  if (!ledger) return null;

  // ── Derived values ──
  const balanceDue    = ledger.balance_due   || 0;
  const creditBalance = ledger.credit_balance || 0;
  const totalInvoiced = ledger.total_invoiced || 0;
  const totalPaid     = ledger.total_paid     || 0;
  const clientName    = ledger.client_name    || '';
  const arCode        = ledger.ar_code        || '';
  const asOf          = ledger.as_of          ? new Date(ledger.as_of) : new Date();

  // Ledger entries — reverse chronological for display
  const allEntries = [...(ledger.ledger || [])].reverse();
  const displayed  = showAll ? allEntries : allEntries.slice(0, 50);

  // Oldest unpaid due date (from ledger, forward direction)
  const forwardEntries = ledger.ledger || [];
  let oldestDue = null;
  if (balanceDue > 0.01) {
    for (const e of forwardEntries) {
      if (e.type === 'invoice' && e.charges > 0 && e.due_date) {
        if (!oldestDue || new Date(e.due_date) < new Date(oldestDue)) {
          oldestDue = e.due_date;
        }
      }
    }
  }

  // Scope jobs from ar-detail
  const scopeJobs = arDetail?.jobs || [];

  return (
    <div style={{ animation: 'fadeIn 0.35s ease-out' }}>
      <style>{GLOBAL_CSS}</style>

      {/* ── Employee account selector ── */}
      {isEmployee && <EmployeeAccountSelector />}

      {/* ── Account Name Header ── */}
      <div style={{ marginBottom: 28 }}>
        <div style={{
          fontSize: 11, fontWeight: 700, letterSpacing: '2px',
          textTransform: 'uppercase', color: TEXT_SECONDARY, marginBottom: 6,
        }}>
          Account {arCode}
        </div>
        <div style={{ fontSize: 24, fontWeight: 700, color: TEXT_PRIMARY, lineHeight: 1.2 }}>
          {clientName}
        </div>
      </div>

      {/* ── 1. Account Status Hero ── */}
      <div style={{
        background: CARD_BG, borderRadius: 16,
        boxShadow: '0 4px 24px rgba(0,0,0,0.07)',
        padding: '48px 40px', textAlign: 'center',
        marginBottom: 16, border: `1px solid ${BORDER}`,
        position: 'relative', overflow: 'hidden',
      }}>
        {/* Gold accent bar */}
        <div style={{
          position: 'absolute', top: 0, left: 0, right: 0, height: 4,
          background: `linear-gradient(90deg, ${NAVY}, #2a5298, ${GOLD})`,
        }} />

        {balanceDue > 0.01 ? (
          <>
            <div className="hero-amount" style={{
              fontSize: 52, fontWeight: 700, color: RED,
              letterSpacing: '-2px', lineHeight: 1.05, marginBottom: 8,
              fontFamily: '"Georgia", "Times New Roman", serif',
            }}>
              {fmtCurrency(balanceDue)}
            </div>
            <div style={{ fontSize: 13, fontWeight: 500, color: TEXT_MUTED, letterSpacing: '2px', textTransform: 'uppercase', marginBottom: 12 }}>
              Balance Due
            </div>
            {oldestDue && (
              <div style={{ fontSize: 14, color: TEXT_SECONDARY }}>
                Due by {fmtDate(oldestDue)}
              </div>
            )}
          </>
        ) : creditBalance > 0.01 ? (
          <>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              background: '#E3F2FD', color: '#1565C0',
              borderRadius: 24, padding: '10px 24px',
              fontSize: 15, fontWeight: 600, marginBottom: 10,
            }}>
              <span style={{ fontSize: 20 }}>ℹ</span>
              <span>You have a credit balance of {fmtCurrency(creditBalance)}</span>
            </div>
            <div style={{ fontSize: 14, color: TEXT_SECONDARY }}>
              This will be applied to your next invoice.
            </div>
          </>
        ) : (
          <>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 10,
              background: '#E8F5E9', color: GREEN,
              borderRadius: 24, padding: '12px 28px',
              fontSize: 16, fontWeight: 600, marginBottom: 10,
            }}>
              <span style={{ fontSize: 22 }}>✓</span>
              <span>All invoices paid</span>
            </div>
            <div style={{ fontSize: 14, color: TEXT_SECONDARY }}>
              Your account is current as of {fmtDate(asOf)}
            </div>
          </>
        )}
      </div>

      {/* ── 2. Three Summary Cards ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 28 }}>
        {[
          { label: 'Contract to Date', value: fmtCurrencyCompact(totalInvoiced), color: TEXT_PRIMARY },
          { label: 'Paid to Date', value: fmtCurrencyCompact(totalPaid), color: GREEN },
          { label: 'Balance Due', value: fmtCurrency(balanceDue), color: balanceDue > 0 ? RED : GREEN },
        ].map(({ label, value, color }) => (
          <div key={label} style={{
            background: CARD_BG, borderRadius: 12, border: `1px solid ${BORDER}`,
            padding: '24px 20px', textAlign: 'center',
            boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
          }}>
            <div style={{ fontSize: 22, fontWeight: 700, color, marginBottom: 6 }}>{value}</div>
            <div style={{ fontSize: 11, fontWeight: 600, color: TEXT_MUTED, letterSpacing: '1.2px', textTransform: 'uppercase' }}>{label}</div>
          </div>
        ))}
      </div>
      <div style={{ fontSize: 12, color: TEXT_MUTED, marginBottom: 24, paddingLeft: 4 }}>As of {fmtDate(asOf)}</div>

      {/* ── Quick Help Bar ── */}
      <div style={{
        display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap',
        marginBottom: 24,
      }}>
        <a href={`mailto:aftercare@audiocommand.com?subject=${encodeURIComponent(`Account Inquiry — ${effectiveArCode || 'Client'}`)}`}
           style={{ padding: '8px 18px', background: NAVY, color: '#fff', borderRadius: 6, textDecoration: 'none', fontSize: 13, fontWeight: 600 }}>
          ✉️ Service Question
        </a>
        <a href={`mailto:cverni@audiocommand.com,dcanavan@audiocommand.com?subject=${encodeURIComponent(`Billing Inquiry — ${effectiveArCode || 'Client'}`)}&bcc=tclancy@audiocommand.com`}
           style={{ padding: '8px 18px', background: '#6A1B9A', color: '#fff', borderRadius: 6, textDecoration: 'none', fontSize: 13, fontWeight: 600 }}>
          💳 Billing Question
        </a>
        <a href="tel:8887243004"
           style={{ padding: '8px 18px', background: LIGHT_BG, color: NAVY, borderRadius: 6, textDecoration: 'none', fontSize: 13, fontWeight: 600, border: `1px solid ${BORDER}` }}>
          📞 (888) 724-3004
        </a>
      </div>

      {/* ── 3. Your Projects (job-grouped) ── */}
      <div style={{
        background: CARD_BG, borderRadius: 12,
        border: `1px solid ${BORDER}`, boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
        overflow: 'hidden', marginBottom: 24,
      }}>
        <div style={{ padding: '22px 24px 0' }}>
          <div style={{
            fontSize: 11, fontWeight: 700, color: TEXT_SECONDARY,
            letterSpacing: '1.5px', textTransform: 'uppercase',
            marginBottom: 16, paddingBottom: 12, borderBottom: `1px solid ${BORDER}`,
          }}>
            Your Projects
          </div>
        </div>

        <JobGroupedStatement scopeJobs={scopeJobs} />
      </div>


      {/* ── 5. Payment Section ── */}
      <div style={{
        background: CARD_BG, borderRadius: 12,
        border: `1px solid ${BORDER}`, boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
        padding: '28px 28px 24px', marginBottom: 24,
      }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: TEXT_SECONDARY, letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: 20, paddingBottom: 12, borderBottom: `1px solid ${BORDER}` }}>
          Payment Options
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 32px', marginBottom: 20 }}>
          {[
            { label: 'Bank',           value: 'Chase Bank, N.A.' },
            { label: 'Routing Number', value: '021000021' },
            { label: 'Account Number', value: '[Contact ACS for details]' },
            { label: 'Account Name',   value: 'Audio Command Systems, Inc.' },
          ].map(({ label, value }) => (
            <div key={label} style={{ paddingTop: 8 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: TEXT_MUTED, letterSpacing: '1.2px', textTransform: 'uppercase', marginBottom: 4 }}>{label}</div>
              <div style={{ fontSize: 14, fontWeight: 500, color: TEXT_PRIMARY, fontFamily: '"Courier New", monospace' }}>{value}</div>
            </div>
          ))}
        </div>

        {arCode && (
          <div style={{
            background: '#F8F6F0', border: `1px solid ${GOLD}40`,
            borderLeft: `3px solid ${GOLD}`, borderRadius: 6,
            padding: '10px 14px', fontSize: 13, color: '#7a6030', marginBottom: 20,
          }}>
            <strong>Important:</strong> Please include your account reference <strong>{arCode}</strong> in the wire memo to ensure proper credit.
          </div>
        )}

        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
          {[
            { href: '/portal/files/ACS-Wire-Transfer-Instructions.pdf', label: '🏦 Wire Transfer Instructions' },
            { href: '/portal/files/ACS-Credit-Card-Authorization.pdf', label: '💳 Credit Card Authorization Form' },
          ].map(({ href, label }) => (
            <a
              key={href}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                padding: '10px 20px', background: LIGHT_BG,
                border: `1px solid ${BORDER}`, borderRadius: 8,
                fontSize: 13, fontWeight: 500, color: NAVY,
                cursor: 'pointer', textDecoration: 'none', fontFamily: 'inherit',
                transition: 'background 0.15s',
              }}
              onMouseEnter={e => e.currentTarget.style.background = '#eaedf2'}
              onMouseLeave={e => e.currentTarget.style.background = LIGHT_BG}
            >
              {label}
            </a>
          ))}
          {!isEmployee && (
            <button
              onClick={() => { setShowPaymentModal(true); setPaymentResult(null); setPaymentFile(null); setPaymentNotes(''); }}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                padding: '10px 20px', background: NAVY, color: '#fff',
                border: 'none', borderRadius: 8,
                fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                transition: 'background 0.15s',
              }}
              onMouseEnter={e => e.currentTarget.style.background = '#2a4a6e'}
              onMouseLeave={e => e.currentTarget.style.background = NAVY}
            >
              📤 Submit Credit Card Form
            </button>
          )}
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            padding: '10px 20px', background: LIGHT_BG,
            border: `1px solid ${BORDER}`, borderRadius: 8,
            fontSize: 13, fontWeight: 500, color: TEXT_MUTED,
            opacity: 0.6, cursor: 'not-allowed',
          }}>
            💰 Pay Online — Coming Soon
          </div>
        </div>

        <div style={{
          background: '#f0f4f8', borderRadius: 6, padding: '10px 14px',
          fontSize: 12, color: TEXT_SECONDARY, lineHeight: 1.6,
        }}>
          <strong>To submit a completed form:</strong> Download the Credit Card Authorization Form above, fill it out, then click "Submit Credit Card Form" to upload it securely. Our finance team will be notified immediately.
          <br />For questions, contact ACS Finance: <strong>cverni@audiocommand.com</strong> · <strong>dcanavan@audiocommand.com</strong> · <strong>ar@audiocommand.com</strong>
        </div>
      </div>

      {/* ── Payment Submission Modal ── */}
      {showPaymentModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', zIndex: 9999,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          animation: 'fadeIn 0.2s ease-out',
        }} onClick={(e) => { if (e.target === e.currentTarget) setShowPaymentModal(false); }}>
          <div style={{
            background: '#fff', borderRadius: 12, padding: '28px 32px',
            width: '100%', maxWidth: 480, boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: NAVY }}>Submit Credit Card Authorization</h3>
              <button onClick={() => setShowPaymentModal(false)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: TEXT_MUTED, padding: 4 }}>✕</button>
            </div>

            {paymentResult ? (
              <div style={{ textAlign: 'center', padding: '20px 0' }}>
                <div style={{ fontSize: 48, marginBottom: 12 }}>{paymentResult.success ? '✅' : '❌'}</div>
                <div style={{ fontSize: 15, color: paymentResult.success ? GREEN : RED, fontWeight: 600, marginBottom: 8 }}>
                  {paymentResult.success ? 'Submitted Successfully' : 'Submission Failed'}
                </div>
                <div style={{ fontSize: 13, color: TEXT_SECONDARY, lineHeight: 1.5 }}>
                  {paymentResult.message}
                </div>
                <button
                  onClick={() => setShowPaymentModal(false)}
                  style={{
                    marginTop: 20, padding: '10px 28px', background: NAVY, color: '#fff',
                    border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                  }}
                >Close</button>
              </div>
            ) : (
              <>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: TEXT_SECONDARY, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 6 }}>Completed Form (PDF)</label>
                  <input
                    type="file"
                    accept="application/pdf"
                    onChange={(e) => setPaymentFile(e.target.files[0] || null)}
                    style={{ fontSize: 13, width: '100%' }}
                  />
                </div>
                <div style={{ marginBottom: 20 }}>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: TEXT_SECONDARY, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 6 }}>Notes (optional)</label>
                  <textarea
                    value={paymentNotes}
                    onChange={(e) => setPaymentNotes(e.target.value)}
                    placeholder="e.g., Please apply to invoice #12345"
                    rows={3}
                    style={{
                      width: '100%', padding: '10px 12px', fontSize: 13, fontFamily: 'inherit',
                      border: `1px solid ${BORDER}`, borderRadius: 6, resize: 'vertical',
                    }}
                  />
                </div>
                <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                  <button
                    onClick={() => setShowPaymentModal(false)}
                    style={{ padding: '10px 20px', background: LIGHT_BG, border: `1px solid ${BORDER}`, borderRadius: 6, fontSize: 13, cursor: 'pointer', color: TEXT_SECONDARY }}
                  >Cancel</button>
                  <button
                    disabled={!paymentFile || paymentSubmitting}
                    onClick={async () => {
                      setPaymentSubmitting(true);
                      try {
                        const formData = new FormData();
                        formData.append('file', paymentFile);
                        if (paymentNotes) formData.append('notes', paymentNotes);
                        const token = localStorage.getItem('client_token');
                        const resp = await fetch('/api/client/submit-payment', {
                          method: 'POST',
                          headers: { Authorization: `Bearer ${token}` },
                          body: formData,
                        });
                        const data = await resp.json();
                        if (resp.ok) {
                          setPaymentResult({ success: true, message: data.message });
                        } else {
                          setPaymentResult({ success: false, message: data.error || 'Submission failed.' });
                        }
                      } catch (err) {
                        setPaymentResult({ success: false, message: 'Network error. Please try again.' });
                      } finally {
                        setPaymentSubmitting(false);
                      }
                    }}
                    style={{
                      padding: '10px 24px', background: !paymentFile ? '#ccc' : NAVY, color: '#fff',
                      border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600,
                      cursor: !paymentFile ? 'not-allowed' : 'pointer', opacity: paymentSubmitting ? 0.7 : 1,
                    }}
                  >{paymentSubmitting ? 'Submitting...' : 'Submit'}</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── 6. Message Thread ── */}
      <MessageThread employeeArCode={isEmployee ? selectedArCode : undefined} />

    </div>
  );
}
